import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow, GameshowNode } from '../../schemas/gameshow.js';

import { asRecord } from '../../activities/types.js';
import { toPointer } from '../../core/issues.js';
import {
  GAMESHOW_QUIZ_ACTIVITY_TYPES,
  GAMESHOW_ROUND_COUNT,
} from '../../schemas/gameshowConstants.js';
import { hasNonEmptyDialogue } from './_helpers.js';

const QUIZ_TYPES = new Set<string>(GAMESHOW_QUIZ_ACTIVITY_TYPES);

export const isRevealNode = (node: GameshowNode): boolean => {
  if (!node.activity) {
    return false;
  }
  if (!QUIZ_TYPES.has(node.activity.type)) {
    return false;
  }
  const setup = (node.activity.setup ?? {}) as Record<string, unknown>;
  return setup.revealAnswer === true;
};

export const revealNodes: Rule<Gameshow> = {
  code: 'gameshow.reveal-nodes',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const nodesById = new Map(input.nodes.map((n, idx) => [n.id, { node: n, idx }]));
    const reveals = input.nodes
      .map((node, idx) => ({ node, idx }))
      .filter(({ node }) => isRevealNode(node));

    const expected = GAMESHOW_ROUND_COUNT * 2;
    if (reveals.length !== expected) {
      const path: (string | number)[] = ['nodes'];
      issues.push({
        code: 'gameshow.reveal-nodes.count',
        severity: 'error',
        message: `Expected ${expected} reveal nodes (one correct + one incorrect per round), found ${reveals.length}.`,
        path,
        pointer: toPointer(path),
        currentValue: reveals.length,
        constraint: { kind: 'range', detail: `exactly ${expected}` },
      });
    }

    for (let i = 1; i <= GAMESHOW_ROUND_COUNT; i++) {
      for (const branch of ['correct', 'incorrect'] as const) {
        const id = `q${i}-${branch}-reveal`;
        if (!nodesById.has(id)) {
          const path: (string | number)[] = ['nodes'];
          issues.push({
            code: 'gameshow.reveal-nodes.missing',
            severity: 'error',
            message: `Missing reveal node '${id}'.`,
            path,
            pointer: toPointer(path),
            constraint: { kind: 'custom', detail: `node '${id}' required` },
          });
        }
      }
    }

    for (const { node, idx } of reveals) {
      const setup = (node.activity?.setup ?? {}) as Record<string, unknown>;

      if (setup.requireCorrectAnswer === true) {
        const path: (string | number)[] = ['nodes', idx, 'activity', 'setup', 'requireCorrectAnswer'];
        issues.push({
          code: 'gameshow.reveal-nodes.require-correct-answer',
          severity: 'error',
          message: `Node '${node.id}': 'requireCorrectAnswer' must be false on reveal nodes (never true, including q${GAMESHOW_ROUND_COUNT}).`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          currentValue: true,
          constraint: { kind: 'enum', allowedValues: ['false', '(omitted)'] },
        });
      }

      if (!hasNonEmptyDialogue(node.narratorDialogue)) {
        const path: (string | number)[] = ['nodes', idx, 'narratorDialogue'];
        issues.push({
          code: 'gameshow.reveal-nodes.missing-narrator',
          severity: 'error',
          message: `Node '${node.id}': reveal node must have narratorDialogue announcing the answer.`,
          path,
          pointer: toPointer(path),
          locator: { nodeId: node.id },
          constraint: { kind: 'shape', detail: 'narratorDialogue required' },
        });
      }

      const match = node.id.match(/^q(\d+)-(correct|incorrect)-reveal$/);
      if (match) {
        const quizId = `q${match[1]}`;
        const quizEntry = nodesById.get(quizId);
        if (quizEntry && quizEntry.node.activity?.type !== node.activity?.type) {
          const path: (string | number)[] = ['nodes', idx, 'activity', 'type'];
          issues.push({
            code: 'gameshow.reveal-nodes.type-mismatch',
            severity: 'error',
            message: `Node '${node.id}': activity type '${node.activity?.type}' must match quiz node '${quizId}' type '${quizEntry.node.activity?.type}'.`,
            path,
            pointer: toPointer(path),
            locator: { nodeId: node.id },
            currentValue: node.activity?.type,
            constraint: { kind: 'enum', allowedValues: [quizEntry.node.activity?.type ?? '(unknown)'] },
          });
        }
      }

      // phrase_matching reveal nodes have a routing footgun:
      // KUIContentStoryAssetActivityPhraseMatching.handleContinue emits
      // `{ outcomeId: correctOption?.outcomeId ?? 'complete' }`. The question
      // node sets the correct option's outcomeId to 'correct' (so submit
      // routes via exitRoutes.correct); if the reveal node inherits that
      // same outcomeId, its Continue button emits 'correct' instead of
      // 'complete' and exitRoutes.complete misses, dead-ending the story.
      if (node.activity?.type === 'phrase_matching') {
        const correctValue = setup.correctAnswer;
        const options = setup.options;
        if (Array.isArray(options)) {
          options.forEach((opt, oIdx) => {
            const o = asRecord(opt);
            const isCorrectOption = typeof o.value === 'string' && o.value === correctValue;
            const outcomeId = typeof o.outcomeId === 'string' ? o.outcomeId : undefined;

            if (isCorrectOption) {
              if (outcomeId !== undefined && outcomeId !== 'complete') {
                const path: (string | number)[] = ['nodes', idx, 'activity', 'setup', 'options', oIdx, 'outcomeId'];
                issues.push({
                  code: 'gameshow.reveal-nodes.phrase-matching-correct-outcome-id',
                  severity: 'error',
                  message: `Node '${node.id}': phrase_matching reveal correct option '${String(o.value)}' has outcomeId '${outcomeId}'. Set it to 'complete' (or omit) — phrase_matching emits the correct option's outcomeId from the Continue button, so anything other than 'complete' breaks routing through exitRoutes.complete.`,
                  path,
                  pointer: toPointer(path),
                  locator: { nodeId: node.id },
                  currentValue: outcomeId,
                  constraint: { kind: 'enum', allowedValues: ['complete', '(omitted)'] },
                });
              }
            }
            else if (outcomeId !== undefined) {
              const path: (string | number)[] = ['nodes', idx, 'activity', 'setup', 'options', oIdx, 'outcomeId'];
              issues.push({
                code: 'gameshow.reveal-nodes.phrase-matching-stray-outcome-id',
                severity: 'error',
                message: `Node '${node.id}': phrase_matching reveal option '${String(o.value)}' carries outcomeId '${outcomeId}'. Wrong options on reveal nodes must not carry outcomeId — they would never fire (reveal pre-fills the correct option) but copying them from the question node is a footgun.`,
                path,
                pointer: toPointer(path),
                locator: { nodeId: node.id },
                currentValue: outcomeId,
                constraint: { kind: 'enum', allowedValues: ['(omitted)'] },
              });
            }
          });
        }

        const routes = node.exitRoutes ?? {};
        if (!routes.complete && !routes.default) {
          const path: (string | number)[] = ['nodes', idx, 'exitRoutes'];
          issues.push({
            code: 'gameshow.reveal-nodes.phrase-matching-missing-complete-route',
            severity: 'error',
            message: `Node '${node.id}': phrase_matching reveal node must have exitRoutes.complete (or exitRoutes.default). The reveal Continue button emits 'complete'.`,
            path,
            pointer: toPointer(path),
            locator: { nodeId: node.id },
            constraint: { kind: 'shape', detail: 'exitRoutes.complete (or .default) required' },
          });
        }
      }
    }

    return issues;
  },
};
